import { useEffect, useState } from 'react';

import { ADMIN_API_BASE, apiClient } from '@infrastructure/api/client';
import { errorText } from '@infrastructure/api/errors';
import { formatDateTime } from '@app/lib/format';

interface Failure {
  id: string;
  recipient: string;
  sender: string;
  status: string;
  error_code: string;
  error_message: string;
  provider: string;
  customer_email: string;
  created_at: string | null;
}

interface DeliveryKPIs {
  totals_24h: { total: number; sent: number; failed: number };
  by_status_24h: Record<string, number>;
  by_provider_24h: Record<string, number>;
  recent_failures: Failure[];
}

function Card({ title, value, sub, tone }: { title: string; value: string | number; sub?: string; tone?: 'neutral' | 'good' | 'bad' }) {
  const accent =
    tone === 'good' ? 'text-green-700'
    : tone === 'bad' ? 'text-red-700'
    : 'text-slate-900';
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <div className="text-xs uppercase tracking-wide text-slate-500">{title}</div>
      <div className={`mt-1 text-2xl font-semibold ${accent}`}>{value}</div>
      {sub && <div className="mt-0.5 text-xs text-slate-500">{sub}</div>}
    </div>
  );
}

export function DeliveryPage() {
  const [data, setData] = useState<DeliveryKPIs | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    apiClient
      .get(`${ADMIN_API_BASE}/delivery/`)
      .then((r) => setData(r.data))
      .catch((e) => setError(errorText(e)));
  }, [refreshKey]);

  if (error) return <div className="text-sm text-red-700">Failed to load delivery KPIs: {error}</div>;
  if (!data) return <div className="text-sm text-slate-500">Loading…</div>;

  const t = data.totals_24h;
  // Of messages with an outcome: pending/queued ones haven't succeeded or failed yet
  const finished = t.sent + t.failed;
  const successRate = finished > 0 ? Math.round((t.sent / finished) * 1000) / 10 : 0;

  return (
    <section>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold">Delivery (last 24h)</h2>
        <button
          type="button"
          onClick={() => setRefreshKey((k) => k + 1)}
          className="rounded border px-3 py-1 text-sm"
        >
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card title="Total messages" value={t.total} />
        <Card title="Sent + delivered" value={t.sent} tone="good" />
        <Card title="Failed / rejected / expired" value={t.failed} tone={t.failed > 0 ? 'bad' : 'neutral'} />
        <Card title="Success rate" value={`${successRate}%`} sub={`of ${finished} finished · ${t.total - finished} still pending`} />
      </div>

      <h3 className="mt-6 mb-2 text-sm font-semibold text-slate-700">By status</h3>
      <div className="flex flex-wrap gap-2 text-sm">
        {Object.entries(data.by_status_24h).map(([k, v]) => (
          <span key={k} className="rounded bg-slate-100 px-2 py-1">
            <strong>{k}</strong>: {v}
          </span>
        ))}
        {Object.keys(data.by_status_24h).length === 0 && (
          <span className="text-slate-500">No traffic in the last 24h.</span>
        )}
      </div>

      <h3 className="mt-6 mb-2 text-sm font-semibold text-slate-700">By provider</h3>
      <div className="flex flex-wrap gap-2 text-sm">
        {Object.entries(data.by_provider_24h).map(([k, v]) => (
          <span key={k} className="rounded bg-slate-100 px-2 py-1">
            <strong>{k}</strong>: {v}
          </span>
        ))}
        {Object.keys(data.by_provider_24h).length === 0 && (
          <span className="text-slate-500">No provider attribution yet.</span>
        )}
      </div>

      <h3 className="mt-6 mb-2 text-sm font-semibold text-slate-700">Recent failures</h3>
      {data.recent_failures.length === 0 ? (
        <p className="text-sm text-slate-500">No failures in the last 24h.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border bg-white">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="px-3 py-2 text-left">When</th>
              <th className="px-3 py-2 text-left">Customer</th>
              <th className="px-3 py-2 text-left">To</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-left">Error</th>
              <th className="px-3 py-2 text-left">Provider</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {data.recent_failures.map((f) => (
              <tr key={f.id}>
                <td className="px-3 py-2 text-xs text-slate-600">
                  {f.created_at ? formatDateTime(f.created_at) : '—'}
                </td>
                <td className="px-3 py-2">{f.customer_email || '—'}</td>
                <td className="px-3 py-2 font-mono text-xs">{f.recipient}</td>
                <td className="px-3 py-2">
                  <span className="rounded bg-red-100 px-2 py-0.5 text-xs text-red-800">
                    {f.status}
                  </span>
                </td>
                <td className="px-3 py-2 text-xs text-slate-600">
                  <code className="font-mono">{f.error_code || ''}</code>{' '}
                  {f.error_message}
                </td>
                <td className="px-3 py-2 text-xs">{f.provider || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </section>
  );
}
